import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cartsService, Carts } from "../services/carts";
import { transactionService } from "../services/transactions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trash2 } from "lucide-react";
import PaymentConfirmationModal from "@/components/PaymentConfirmationModal";
import PaymentStatusModal from "@/components/PaymentStatusModal";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import Skeleton from "react-loading-skeleton";

function getTimeRange(dateTimeStr: string): string {
  // Convert string to Date
  const date = new Date(dateTimeStr.replace(" ", "T"));

  // Copy and subtract 30 seconds
  const earlier = new Date(date.getTime() - 30 * 1000);

  // Format to HH:mm:ss
  const formatTime = (d: Date) => d.toTimeString().split(" ")[0]; // "12:09:40"

  return `${formatTime(earlier)} - ${formatTime(date)}`;
}

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const [carts, setCarts] = useState<Carts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  const [isStatusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("dana");
  const [totalPrice, setTotalPrice] = useState(0);
  const [pendingTransaction, setPendingTransaction] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [invoiceId, setInvoiceId] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [shouldCheckPayment, setShouldCheckPayment] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState<string | null>(
    null
  );
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  async function getXenditPayment(invoiceId: string) {
    if (isCheckingStatus) return;

    try {
      const response = await transactionService.getPaymentStatus(invoiceId);

      if (
        ["EXPIRED", "PAID", "SUCCESS", "SETTLED", "FAILED"].includes(
          response.status
        )
      ) {
        const updateResponse = await transactionService.updateTransactionStatus(
          String(transactionId),
          response.status.toLowerCase()
        );

        if (updateResponse.success === 1 && response.status != "EXPIRED") {
          setShowSuccessPopup(true);
        }

        setShouldCheckPayment(false);
        transactionPending();
      }
    } catch (error) {
      toast({
        title: "Get Payment Status Failed",
        description: "Get payment status failed",
      });
    }
  }

  async function transactionPending() {
    try {
      const response =
        await transactionService.fetchTransactionsByUserIdPending();
      setPendingTransaction(response.transactions);
      localStorage.setItem("pendingTransaction", JSON.stringify(response.transactions));
      return response.transactions; // return the array
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async function checkPendingTransactionStatus(
    transactions = pendingTransaction
  ) {
    if (transactions && transactions.length > 0) {
      console.log("asuk checkPendingTransactionStatus IFNYA");
      try {
        setIsCheckingStatus(true);
        const transaction = transactions[0];
        const response = await transactionService.checkTransactionStatus(
          transaction.id
        );
        const detail = response.transaction;
        setTransactionId(transaction.id);
        console.log("transaction.id: ", transaction.id);
        setTransactionStatus(detail.status);
        console.log("detail.status: ", detail.status);
        setInvoiceId(detail.invoice_id);
        console.log("invoiceId: ", invoiceId);

        // If transaction is completed/paid, refresh the pending transactions
        if (detail.status === "SETTLED" || response.status === "EXPIRED") {
          await transactionPending();
        }
      } catch (error) {
        console.error("Error checking transaction status:", error);
      } finally {
        setIsCheckingStatus(false);
      }
    }
  }

  useEffect(() => {
    if (pendingTransaction.length == 0) return; // prevent running if variable is empty/undefined

    // run immediately when variable changes
    getXenditPayment(invoiceId);

    // then run every 3 seconds
    const interval = setInterval(() => {
      getXenditPayment(invoiceId);
    }, 3000);

    // cleanup when variable changes or component unmounts
    return () => clearInterval(interval);
  }, [pendingTransaction.length > 0]); // 👈 rerun effect whenever this variable changes

  // fetch pending on mount
  useEffect(() => {
    const run = async () => {
      const txs = await transactionPending();
      if (txs.length > 0) {
        checkPendingTransactionStatus(txs);
      }
    };
    run();
  }, []);

  // check xendit payment status when requested
  useEffect(() => {
    if (shouldCheckPayment && invoiceId) {
      getXenditPayment(invoiceId);
    }
  }, [shouldCheckPayment, invoiceId]);

  // start a single interval when there is a pending transaction; stop when none
  useEffect(() => {
    if (pendingTransaction.length > 0 && !intervalRef.current) {
      // immediate check
      getXenditPayment(invoiceId);

      intervalRef.current = setInterval(() => {
        getXenditPayment(invoiceId);
      }, 3000);
    }

    if (pendingTransaction.length === 0 && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [pendingTransaction.length]);

  useEffect(() => {
    fetchCarts();
  }, []);

  const fetchCarts = async () => {
    try {
      const data = await cartsService.fetchCartsByUserId();
      setCarts(data.cart_items);
      setTotalPrice(data.cart_items.reduce((sum, cart) => sum + cart.price, 0));
    } catch (err) {
      setError("Failed to fetch cart items");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleProceedToCheckout = () => {
    setPaymentModalOpen(true);
  };

  const handlePaymentConfirm = async (paymentMethod: string) => {
    setSelectedPaymentMethod(paymentMethod);
    setPaymentModalOpen(false);

    // "Pay Now" button action
    try {
      const payload = {
        cart_items: carts.map(({ id, ...rest }) => ({
          cart_id: id, // rename only here
          ...rest,
        })),
        voucher_id: 0, // TODO: Sementara kosong, isi nanti dengan voucher_id dari backend
      };

      const response = await transactionService.createTransaction(payload);
      await transactionPending();
      console.log(response);

      setShouldCheckPayment(true);
      setTransactionId(response.uuid);
      setInvoiceId(response.invoiceId);
      toast({
        title: "Payment successful",
        description: `Transaction #${response.uuid} waiting for your payment...`,
      });

      setStatusModalOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Terjadi kesalahan. Mohon coba lagi.",
        variant: "destructive",
      });
      setStatusModalOpen(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100">
        <main className="max-w-5xl mx-auto px-4 py-6">

          {/* Header Skeleton */}
          <div className="mb-8">
            <Skeleton width={120} height={24} />
            <Skeleton width={200} height={14} className="mt-2" />
          </div>

          {/* Cart Items Skeleton */}
          <div className="space-y-4 mb-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border bg-white/80 backdrop-blur shadow-sm p-4"
              >
                <div className="space-y-4">

                  {/* Title */}
                  <Skeleton width="70%" height={18} />

                  {/* Details stacked (mobile style) */}
                  <div className="space-y-2">
                    <Skeleton height={12} />
                    <Skeleton height={12} />
                    <Skeleton height={12} />
                    <Skeleton height={12} />
                  </div>

                  {/* Price + Delete row */}
                  <div className="flex justify-between items-center pt-2">
                    <Skeleton width={90} height={22} />
                    <Skeleton circle width={32} height={32} />
                  </div>

                </div>
              </div>
            ))}
          </div>

          {/* Order Summary Skeleton */}
          <div className="rounded-2xl bg-white/80 backdrop-blur p-4 shadow-sm mb-8">
            <Skeleton width={140} height={18} />
            <Skeleton width={100} height={12} className="mt-2" />
            <Skeleton width={120} height={28} className="mt-4" />
          </div>

          {/* Buttons Skeleton (stacked on mobile) */}
          <div className="space-y-3 mb-10">
            <Skeleton height={44} className="rounded-xl" />
            <Skeleton height={44} className="rounded-xl" />
          </div>

          {/* Pending Transaction Skeleton */}
          <div className="pt-6">
            <Skeleton width={180} height={20} className="mb-4" />
            <div className="rounded-2xl bg-white/80 backdrop-blur shadow-sm p-4 space-y-3">
              <Skeleton width="60%" height={16} />
              <Skeleton height={12} />
              <Skeleton height={12} />
              <Skeleton height={12} />
            </div>
          </div>

        </main>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );

  function handleRemove(cartID: string): void {
    try {
      cartsService.deleteCart(cartID).then(async () => {
        fetchCarts();
        toast({
          title: "Clip Removed",
          description: "Clip Successfully Removed from Cart",
        });
      });
    } catch (error) {
      console.error("Failed to delete cart:", error);
    }
  }

  function handleRemoveAll(): void {
    try {
      cartsService.clearCart().then(async () => {
        fetchCarts();
        toast({
          title: "Cart Cleared.",
        });
      });
    } catch (error) { }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100">
      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/brands")}
                className="mb-4 rounded-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              <h1 className="text-3xl font-bold tracking-tight">
                Your Cart
              </h1>
              <p className="text-muted-foreground text-sm">
                Review your selected clips before checkout
              </p>
            </div>

            {pendingTransaction.length === 0 && carts.length > 0 && (
              <Button
                variant="outline"
                onClick={handleRemoveAll}
                className="rounded-lg"
              >
                Clear All
              </Button>
            )}
          </div>
        </div>
        {/* Cart Items */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          {carts.map((cart) => (
            <Card
              key={cart.id}
              className="rounded-2xl border bg-white/80 backdrop-blur shadow-sm hover:shadow-md transition"
            >
              <CardContent className="p-6">
                <div className="flex justify-between gap-6">
                  {/* Left */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">
                      {cart.court_name}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                      <p><span className="font-medium">Address:</span> {cart.merchant_address}</p>
                      <p><span className="font-medium">Merchant:</span> {cart.merchant_name}</p>
                      <p><span className="font-medium">Clip Date:</span> {new Date(cart.created_at).toLocaleDateString()}</p>
                      <p><span className="font-medium">Timestamp:</span> {getTimeRange(cart.created_at)}</p>
                      <p><span className="font-medium">Downloads:</span> {cart.download_count}</p>
                      <p>
                        <span className="font-medium">Status:</span>{" "}
                        <span className={cart.bought ? "text-green-600" : "text-yellow-600"}>
                          {cart.bought ? "Purchased" : "In Cart"}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Right */}
                  <div className="flex flex-col items-end justify-between">
                    <p className="text-2xl font-bold text-primary">
                      {formatPrice(cart.price)}
                    </p>

                    {pendingTransaction.length === 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemove(cart.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={20} />
                      </Button>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {carts.length === 0 && (
            <div className="text-center py-20">
              <h3 className="text-xl font-semibold mb-2">
                Your cart is empty
              </h3>
              <p className="text-muted-foreground mb-6">
                Browse courts and add clips to your cart.
              </p>
              <Button onClick={() => navigate("/brands")}>
                Browse Courts
              </Button>
            </div>
          )}
        </div>

        {/* Total Price Section */}
        {carts.length > 0 && (
          <div className="rounded-2xl bg-white/80 backdrop-blur p-6 shadow-sm mb-10">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Order Summary</h3>
                <p className="text-sm text-muted-foreground">
                  {carts.length} item{carts.length !== 1 ? "s" : ""}
                </p>
              </div>

              <p className="text-3xl font-bold text-primary">
                {formatPrice(totalPrice)}
              </p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mb-12">
          {pendingTransaction.length === 0 && (

            <Button
              variant="outline"
              onClick={() => navigate("/brands")}
              className="flex-1 rounded-xl"
            >
              Continue Shopping
            </Button>
          )}

          {carts.length > 0 &&
            (pendingTransaction && pendingTransaction.length > 0 ? (
              <Button
                className="flex-1 rounded-xl"
                onClick={() => setStatusModalOpen(true)}
              >
                Check Payment Status
              </Button>
            ) : (
              <Button
                className="flex-1 rounded-xl"
                onClick={handleProceedToCheckout}
              >
                Proceed to Checkout
              </Button>
            ))}
        </div>

        <div className="mb-8 pt-10">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Pending Transaction</h2>
            {pendingTransaction && pendingTransaction.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => getXenditPayment(invoiceId)}
                disabled={isCheckingStatus}
              >
                Refresh Status
              </Button>
            )}
          </div>
          {transactionLoading ? (
            <p>Loading pending transactions...</p>
          ) : !pendingTransaction[0] ? (
            <p>No pending transactions.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              <Card className="rounded-2xl bg-white/80 backdrop-blur shadow-sm">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        Transaction #{pendingTransaction[0].id}
                      </h3>

                      <div className="space-y-1 text-sm text-muted-foreground">
                        <p>Subtotal: {formatPrice(pendingTransaction[0].subtotal)}</p>
                        <p>Discount: {formatPrice(pendingTransaction[0].discount)}</p>
                        <p className="font-medium text-foreground">
                          Grand Total: {formatPrice(pendingTransaction[0].grand_total)}
                        </p>
                        <p>
                          Status:{" "}
                          <span className="text-yellow-600 font-medium">
                            {transactionStatus ||
                              (!pendingTransaction[0].paid_at
                                ? "PENDING"
                                : "PAID")}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* go to library button */}
        <div className="bg-muted rounded-lg mb-8 mt-8">
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => navigate("/library")}
              className="flex-1"
            >
              Go to library
            </Button>
          </div>
        </div>
      </main>

      {/* Modals */}
      <PaymentConfirmationModal
        isOpen={isPaymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onPaymentConfirm={handlePaymentConfirm}
        totalPrice={totalPrice}
      />
      <PaymentStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        paymentMethod={selectedPaymentMethod}
        totalPrice={totalPrice}
      />
      <Dialog open={showSuccessPopup} onOpenChange={setShowSuccessPopup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment Successful</DialogTitle>
          </DialogHeader>
          <p>Your transaction has been successfully paid.</p>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowSuccessPopup(false);
                navigate("/library");
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cart;
